import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { QUERY_KEYS } from '~shared/api/query-keys';

export interface AgingBucket {
  label: string;
  amount: number;
  percentage: number;
}

export interface OutstandingPayment {
  /** Subscription invoice id (stable row key) */
  invoiceId: string | null;
  /** Legacy single name; prefer parentName + studentName */
  studentOrParentName: string;
  parentName: string;
  studentName: string;
  amountDue: number;
  daysOverdue: number;
  dueDate: string | null;
  status: string;
  /** Resolved parent id for linking to parent transactions (direct or via student) */
  parentId: string | null;
  /** Student id for linking to student details */
  studentId: string | null;
}

export interface OutstandingOverviewPagination {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

export interface DashboardOutstandingOverviewResponse {
  totalOutstandingBalance: number;
  agingBuckets: Array<AgingBucket>;
  outstandingPayments: Array<OutstandingPayment>;
  pagination: OutstandingOverviewPagination;
}

export interface OutstandingOverviewParams {
  page?: number;
  limit?: number;
  sortBy?: 'daysOverdue' | 'parentName' | 'studentName' | 'dueDate';
  sortOrder?: 'asc' | 'desc';
  /** Server-side filter on parent or student name */
  q?: string;
}

export const dashboardOutstandingOverviewAPI = async (
  params?: OutstandingOverviewParams,
) => {
  const searchParams = new URLSearchParams();
  if (params?.page != null) searchParams.set('page', String(params.page));
  if (params?.limit != null) searchParams.set('limit', String(params.limit));
  if (params?.sortBy != null) searchParams.set('sortBy', params.sortBy);
  if (params?.sortOrder != null) searchParams.set('sortOrder', params.sortOrder);
  if (params?.q != null && params.q.trim() !== '')
    searchParams.set('q', params.q.trim());
  const query = searchParams.toString();
  const url = query
    ? `/dashboard/outstanding-overview?${query}`
    : '/dashboard/outstanding-overview';
  const response = await baseAPI.get<DashboardOutstandingOverviewResponse>(url);
  return response.data;
};

export const useDashboardOutstandingOverview = (
  params?: OutstandingOverviewParams,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.DASHBOARD_OUTSTANDING_OVERVIEW(params),
    queryFn: () => dashboardOutstandingOverviewAPI(params),
    placeholderData: keepPreviousData,
  });
};
